"use client";

import { useEffect, useId, useState } from "react";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { letterGrade, scoreTone } from "~/lib/siteglue/agent-ready/results-groups";
import { RESULTS_STATUS } from "~/lib/siteglue/landing-redesign/copy";
import {
  HOMEPAGE_PREVIEW_INTERVAL_MS,
  HOMEPAGE_PREVIEW_SLIDES,
  homepagePreviewSampleLabel,
  homepagePreviewShouldAutoplay,
  nextHomepagePreviewSlide,
  previousHomepagePreviewSlide,
  type HomepagePreviewSlide,
  type HomepagePreviewSlideId,
} from "~/lib/siteglue/landing-redesign/homepage-hero";

const TONE_TEXT = {
  ready: "text-emerald-600",
  partly: "text-amber-600",
  not: "text-red-600",
  empty: "text-slate-400",
} as const;

const TONE_BAR = {
  ready: "bg-emerald-500",
  partly: "bg-amber-500",
  not: "bg-red-500",
  empty: "bg-slate-300",
} as const;

const TONE_RESULT = {
  Verified: "text-emerald-700",
  Recommended: "text-amber-700",
} as const;

function PreviewCard({ slide }: { slide: HomepagePreviewSlide }) {
  const tone = scoreTone(slide.score);
  const grade = letterGrade(slide.score);

  return (
    <article className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
            <Link href={slide.href} className="hover:text-slate-800 hover:underline">
              {slide.kicker}
            </Link>
          </p>
          <p className="mt-2 text-sm font-semibold text-slate-800">{RESULTS_STATUS(slide.score)}</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">{slide.summary}</p>
        </div>
        <div className="flex items-end gap-4">
          <p className="text-5xl font-semibold tracking-tight text-slate-900">
            {slide.score}
            <span className="text-lg font-medium text-slate-400">/100</span>
          </p>
          <p className={`text-4xl font-semibold tracking-tight ${TONE_TEXT[tone]}`}>{grade}</p>
        </div>
      </div>
      <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {slide.scoreLabel}
      </p>
      <div className="mt-3 space-y-3">
        {slide.rows.map((row) => (
          <div key={row.id} className="rounded-xl border border-slate-100 px-3 py-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-slate-800">{row.label}</p>
              <span className="flex shrink-0 items-center gap-2">
                <p className={`text-sm font-semibold ${TONE_TEXT[scoreTone(row.score)]}`}>
                  {row.score}/100
                </p>
                <p className={`text-xs font-semibold ${TONE_RESULT[row.result]}`}>{row.result}</p>
              </span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
              <div
                className={`h-full rounded-full ${TONE_BAR[scoreTone(row.score)]}`}
                style={{ width: `${Math.max(row.score, 4)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </article>
  );
}

export function HomeHeroReportPreview() {
  const labelId = useId();
  const [active, setActive] = useState<HomepagePreviewSlideId>("aro");
  const [paused, setPaused] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReducedMotion(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    const sync = () => setPageVisible(document.visibilityState === "visible");
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => document.removeEventListener("visibilitychange", sync);
  }, []);

  useEffect(() => {
    if (!homepagePreviewShouldAutoplay({ reducedMotion, paused, pageVisible })) return;
    const timer = window.setInterval(() => {
      setActive((current) => nextHomepagePreviewSlide(current));
    }, HOMEPAGE_PREVIEW_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [paused, pageVisible, reducedMotion]);

  return (
    <div
      className="w-full"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setPaused(false);
        }
      }}
    >
      <div className="overflow-hidden rounded-[1.6rem] border border-slate-200 bg-slate-100 shadow-[0_18px_50px_rgba(15,23,42,0.12)]">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-2.5">
          <div className="flex items-center gap-1.5" aria-hidden>
            <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
            <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
            <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
          </div>
          <p id={labelId} className="truncate text-xs font-medium text-slate-500">
            Readiness Report · Preview
          </p>
          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
            Sample
          </span>
        </div>
        <div className="bg-[#f7f6f3] p-3 sm:p-4">
          <p className="mb-3 text-center text-xs font-medium text-slate-500">
            {homepagePreviewSampleLabel()}
          </p>
          <div className="grid" aria-labelledby={labelId} aria-live="polite">
            {HOMEPAGE_PREVIEW_SLIDES.map((slide) => (
              <div
                key={slide.id}
                className={[
                  "col-start-1 row-start-1",
                  reducedMotion ? "" : "transition-opacity duration-500 ease-out",
                  active === slide.id ? "opacity-100" : "pointer-events-none opacity-0",
                ].join(" ")}
                aria-hidden={active !== slide.id}
              >
                <PreviewCard slide={slide} />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-center gap-3">
        <button
          type="button"
          aria-label="Previous report preview"
          onClick={() => setActive((current) => previousHomepagePreviewSlide(current))}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </button>
        <div className="flex items-center gap-2" role="tablist" aria-label="Report preview">
          {HOMEPAGE_PREVIEW_SLIDES.map((slide) => (
            <button
              key={slide.id}
              type="button"
              role="tab"
              aria-selected={active === slide.id}
              aria-label={slide.kicker}
              onClick={() => setActive(slide.id)}
              className={`h-2.5 rounded-full transition-all ${
                active === slide.id ? "w-6 bg-[#4f81bd]" : "w-2.5 bg-slate-300 hover:bg-slate-400"
              }`}
            />
          ))}
        </div>
        <button
          type="button"
          aria-label="Next report preview"
          onClick={() => setActive((current) => nextHomepagePreviewSlide(current))}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
        >
          <ChevronRight className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
